import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface IngredientCategory {
  _id: string;
  name_en: string;
  name_ar: string;
  slug: string;
  order: number;
  active: boolean;
  icon?: string;
  description?: string;
  description_ar?: string;
}

export default function IngredientCategoriesTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<IngredientCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await api.get('/ingredients/categories');
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setCategories(data);
    } catch (err) {
      console.error(err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm(t('pantryCategories.confirmDelete'))) return;
    try {
      await api.delete(`/ingredients/categories/${id}`);
      loadCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || t('pantryCategories.failedToDelete'));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('pantryCategories.title')}</h3>
            <p className="text-sm text-gray-500">{t('pantryCategories.description')}</p>
          </div>
          <button
            onClick={() => navigate('/pantry-categories/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('pantryCategories.addCategory')}</span>
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('pantryCategories.loading')}</div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('pantryCategories.noCategories')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryCategories.nameEn')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryCategories.nameAr')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryCategories.slug')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('pantryCategories.order')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('units.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((cat) => (
                <tr key={cat._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cat.name_en || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" dir="rtl">{cat.name_ar || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cat.slug || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cat.order ?? 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${cat.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {cat.active ? t('units.active') : t('units.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => navigate(`/pantry-categories/${cat._id}/edit`)}
                        className="text-primary-orange hover:text-orange-600"
                        title={t('common.edit')}
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat._id)}
                        className="text-red-600 hover:text-red-800"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
